import {
  Badge,
  Button,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import type {
  SalaryAdvanceListItem,
  SalaryAdvanceStatus,
} from "@/modules/hr/salary-advance/domain/contracts/types";
import { formatDateLocal } from "@/shared/lib/date-time";

const STATUS_LABEL: Record<SalaryAdvanceStatus, string> = {
  pending: "ລໍຖ້າອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  rejected: "ປະຕິເສດ",
  paid: "ຈ່າຍແລ້ວ",
  cancelled: "ຍົກເລີກ",
  fully_repaid: "ຫັກຄືນຄົບ",
};

const STATUS_DESC: Record<SalaryAdvanceStatus, string> = {
  pending: "ລໍຖ້າການອະນຸມັດ",
  approved: "ໄດ້ຮັບການອະນຸມັດ — ລໍຖ້າໂອນເງິນ",
  rejected: "ຄຳຂໍຖືກປະຕິເສດ",
  paid: "ໂອນເງິນແລ້ວ — ກຳລັງຫັກຄືນລາຍເດືອນ",
  cancelled: "ຄຳຂໍຖືກຍົກເລີກ",
  fully_repaid: "ຫັກຄືນຄົບແລ້ວ",
};

function statusVariant(
  s: SalaryAdvanceStatus,
): "default" | "secondary" | "destructive" | "outline" {
  if (s === "approved" || s === "paid") return "default";
  if (s === "rejected" || s === "cancelled") return "destructive";
  if (s === "fully_repaid") return "outline";
  return "secondary";
}

function formatLAK(amount: number) {
  return `${new Intl.NumberFormat("lo-LA").format(amount)} ກີບ`;
}

type Props = {
  data: SalaryAdvanceListItem[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onViewDetail: (row: SalaryAdvanceListItem) => void;
};

export function MyAdvanceListTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onViewDetail,
}: Props) {
  const columns: TanstackReactTable.ColumnDef<SalaryAdvanceListItem>[] = [
    createSortableColumn<SalaryAdvanceListItem>("requestDate", "ວັນທີ່ຍື່ນ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDateLocal(row.original.requestDate)}
        </span>
      ),
    }),
    createSortableColumn<SalaryAdvanceListItem>("amount", "ຈຳນວນທີ່ຂໍ", {
      size: 150,
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">
          {formatLAK(row.original.amount)}
        </span>
      ),
    }),
    {
      id: "approvedAmount",
      header: "ຈຳນວນທີ່ໄດ້ຮັບ",
      size: 150,
      enableSorting: false,
      cell: ({ row }) =>
        row.original.approvedAmount != null ? (
          <span className="tabular-nums">
            {formatLAK(row.original.approvedAmount)}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    createSortableColumn<SalaryAdvanceListItem>("status", "ສະຖານະ", {
      size: 170,
      cell: ({ row }) => {
        const s = row.original.status;
        return (
          <div>
            <Badge variant={statusVariant(s)}>{STATUS_LABEL[s]}</Badge>
            <div className="mt-0.5 text-muted-foreground text-xs">
              {STATUS_DESC[s]}
            </div>
          </div>
        );
      },
    }),
    {
      id: "actions",
      header: "ການດຳເນີນການ",
      size: 100,
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 font-normal"
          onClick={() => onViewDetail(row.original)}
        >
          ລາຍລະອຽດ
        </Button>
      ),
    },
  ];

  return (
    <DataTable<SalaryAdvanceListItem, unknown>
      noDataMessage="ຍັງບໍ່ມີຄຳຂໍ."
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (!sorting[0]?.id) return;
        onSortingChange(sorting[0].id, !!sorting[0].desc);
      }}
    />
  );
}
